/**
 * DraftEditorInline - Inline draft editor for workspace view mode
 *
 * Receives draft content as props from MainWorkspace lifted state.
 * No dependency on panelState.draftActive — just a workspace view mode.
 *
 * Features:
 * - Textarea with auto-save (1s debounce)
 * - PatchToolbar for selection-based edits
 * - Collapsible source cards sidebar
 * - Word count + dirty indicator
 * - "View with Sources" button
 *
 * @module @humanizer/studio/components/workspace/DraftEditorInline
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PatchToolbar } from './PatchToolbar';
import { SourceCards } from './SourceCards';
import { VersionBar, type ContentVersion } from './VersionBar';
import type { DraftProvenance, DraftQualityReport } from './OutlinePanel';
import { createLogger } from '../../utils/debug';
import '../../styles/guided-writing.css';

const log = createLogger('DraftEditor');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface DraftEditorInlineProps {
  draftText: string;
  onDraftTextChange: (text: string) => void;
  onFlush?: (text: string) => void;
  sourceCards: Array<{ title: string; excerpt: string }>;
  provenances?: DraftProvenance[];
  onViewWithSources?: (text: string, cards: Array<{ title: string; excerpt: string }>, provenances?: DraftProvenance[]) => void;
  generationModel?: string;
  humanizationModel?: string;
  humanized?: boolean;
  generationTimeMs?: number;
  humanizationTimeMs?: number;
  targetWordCount?: number;
  /** Buffer ID for version history lookups */
  draftBufferId?: string | null;
  /** Incremented when a new draft generation creates a checkpoint */
  versionRefreshTrigger?: number;
  /** Quality report from Engine B constraint signals */
  qualityReport?: DraftQualityReport | null;
  /** Noema-noesis intentional drift report */
  intentionalDrift?: {
    objectsLost: Array<{ label: string; salience: number }>;
    objectsAdded: Array<{ label: string }>;
    stanceShifts: Array<{ label: string; from: string; to: string }>;
    tensionPreserved: number;
    overallDrift: number;
  } | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function DraftEditorInline({
  draftText,
  onDraftTextChange,
  onFlush,
  sourceCards,
  provenances,
  onViewWithSources,
  generationModel,
  humanizationModel,
  humanized,
  generationTimeMs,
  humanizationTimeMs,
  targetWordCount,
  draftBufferId,
  versionRefreshTrigger,
  qualityReport,
  intentionalDrift,
}: DraftEditorInlineProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Local editing state (debounced write-through to parent)
  const [localText, setLocalText] = useState(draftText);
  const [isDirty, setIsDirty] = useState(false);
  const [showSourceCards, setShowSourceCards] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for unmount flush (synced every render, not via useEffect)
  const localTextRef = useRef(localText);
  localTextRef.current = localText;
  const onDraftTextChangeRef = useRef(onDraftTextChange);
  onDraftTextChangeRef.current = onDraftTextChange;
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;

  // ─── Version history ────────────────────────────────────────────
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const hasCheckpointedSinceLoad = useRef(false);

  // Save the "live" draft text before switching to a version preview.
  // This prevents auto-save from overwriting the buffer with old version text.
  const currentDraftRef = useRef(draftText);

  // Keep ref in sync when on __current__ (from user edits or external updates)
  useEffect(() => {
    if (activeVersionId === '__current__' || activeVersionId === null) {
      currentDraftRef.current = localTextRef.current;
    }
  }, [localText, activeVersionId]);

  // Also sync when draftText changes externally (new generation)
  useEffect(() => {
    currentDraftRef.current = draftText;
  }, [draftText]);

  // Reset checkpoint flag when draftText changes externally (new generation or version restore)
  const prevDraftText = useRef(draftText);
  useEffect(() => {
    if (draftText !== prevDraftText.current) {
      prevDraftText.current = draftText;
      hasCheckpointedSinceLoad.current = false;
    }
  }, [draftText]);

  // Load versions when buffer ID or refresh trigger changes
  useEffect(() => {
    if (!draftBufferId || !window.electronBuffer?.listVersions) {
      setVersions([]);
      setActiveVersionId(null);
      return;
    }
    window.electronBuffer.listVersions(draftBufferId).then(rawVersions => {
      const mapped: ContentVersion[] = rawVersions.map(v => ({
        id: v.id,
        type: 'checkpoint' as const,
        label: v.label || 'Checkpoint',
        timestamp: v.createdAt,
        wordCount: v.wordCount,
      }));
      // Add "Current" entry as the latest
      mapped.push({
        id: '__current__',
        type: 'archive' as const,
        label: 'Current',
        timestamp: Date.now(),
      });
      setVersions(mapped);
      setActiveVersionId('__current__');
    }).catch(err => {
      log.warn('Failed to load versions:', err);
      setVersions([]);
    });
  }, [draftBufferId, versionRefreshTrigger]);

  // Version select: load version text into the editor (preview only — does NOT auto-save)
  const handleVersionSelect = useCallback(async (versionId: string) => {
    // Save current text before switching away from __current__
    if (activeVersionId === '__current__' || activeVersionId === null) {
      currentDraftRef.current = localTextRef.current;
    }

    setActiveVersionId(versionId);

    if (versionId === '__current__') {
      // Restore the live draft text that was saved before preview
      const restored = currentDraftRef.current;
      setLocalText(restored);
      onDraftTextChange(restored);
      return;
    }

    if (!window.electronBuffer?.loadVersion) return;
    try {
      const ver = await window.electronBuffer.loadVersion(versionId);
      if (ver) {
        // Only update local display — do NOT call onDraftTextChange.
        // This prevents MainWorkspace auto-save from overwriting the buffer
        // with old version text while the user is just previewing.
        setLocalText(ver.text);
      }
    } catch (err) {
      log.warn('Failed to load version:', err);
    }
  }, [activeVersionId, onDraftTextChange]);

  // Version restore (right-click): restore + reload list
  const handleVersionRestore = useCallback(async (versionId: string) => {
    if (!draftBufferId || !window.electronBuffer?.restoreVersion) return;
    try {
      await window.electronBuffer.restoreVersion(draftBufferId, versionId);
      // Reload the version into editor
      const ver = await window.electronBuffer.loadVersion(versionId);
      if (ver) {
        setLocalText(ver.text);
        onDraftTextChange(ver.text);
        currentDraftRef.current = ver.text;
      }
      // Reload version list
      const rawVersions = await window.electronBuffer.listVersions(draftBufferId);
      const mapped: ContentVersion[] = rawVersions.map(v => ({
        id: v.id,
        type: 'checkpoint' as const,
        label: v.label || 'Checkpoint',
        timestamp: v.createdAt,
        wordCount: v.wordCount,
      }));
      mapped.push({
        id: '__current__',
        type: 'archive' as const,
        label: 'Current',
        timestamp: Date.now(),
      });
      setVersions(mapped);
      setActiveVersionId('__current__');
    } catch (err) {
      log.warn('Failed to restore version:', err);
    }
  }, [draftBufferId, onDraftTextChange]);

  // Checkpoint on first keystroke after loading/switching
  const checkpointOnFirstEdit = useCallback(() => {
    if (hasCheckpointedSinceLoad.current) return;
    if (!draftBufferId || !window.electronBuffer?.createVersion) return;
    hasCheckpointedSinceLoad.current = true;
    window.electronBuffer.createVersion(draftBufferId, 'Before editing', undefined, 'auto').catch(err => {
      log.warn('Failed to checkpoint before editing:', err);
    });
  }, [draftBufferId]);

  // Selection state for patch toolbar
  const [selection, setSelection] = useState<{
    text: string;
    rect: DOMRect;
    start: number;
    end: number;
  } | null>(null);

  // Sync from parent when draftText changes externally (e.g. new draft generated)
  useEffect(() => {
    setLocalText(draftText);
    setIsDirty(false);
  }, [draftText]);

  // Debounced write-through (with checkpoint on first edit)
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setLocalText(text);
    setIsDirty(true);
    checkpointOnFirstEdit();

    // If editing while previewing a version, adopt it as the new current
    // so clicking "Current" won't revert to stale text
    if (activeVersionId !== '__current__' && activeVersionId !== null) {
      currentDraftRef.current = text;
      setActiveVersionId('__current__');
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onDraftTextChange(text);
      setIsDirty(false);
    }, 1000);
  }, [onDraftTextChange, checkpointOnFirstEdit, activeVersionId]);

  // Cleanup on unmount — flush pending changes
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // Scenario A: setState works when parent stays mounted (view-mode switch)
      onDraftTextChangeRef.current(localTextRef.current);
      // Scenario B: update parent ref for parent's own unmount save (route change)
      onFlushRef.current?.(localTextRef.current);
    };
  }, []);

  // Handle text selection for patch toolbar
  const handleSelect = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localText.slice(start, end);

    if (selectedText.length > 0) {
      const rect = textarea.getBoundingClientRect();
      setSelection({ text: selectedText, rect, start, end });
    } else {
      setSelection(null);
    }
  }, [localText]);

  // Handle patch result — replace selected text
  const handlePatchApply = useCallback((newText: string) => {
    if (!selection) return;
    const updated = localText.slice(0, selection.start) + newText + localText.slice(selection.end);
    setLocalText(updated);
    setSelection(null);
    setIsDirty(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onDraftTextChange(updated);
      setIsDirty(false);
    }, 1000);
  }, [selection, localText, onDraftTextChange]);

  // Get surrounding context for patch
  const getPatchContext = useCallback(() => {
    if (!selection) return { before: '', after: '' };

    const beforeText = localText.slice(0, selection.start);
    const afterText = localText.slice(selection.end);

    const paraBreak = beforeText.lastIndexOf('\n\n');
    const before = paraBreak >= 0 ? beforeText.slice(paraBreak + 2) : beforeText.slice(-200);

    const paraEnd = afterText.indexOf('\n\n');
    const after = paraEnd >= 0 ? afterText.slice(0, paraEnd) : afterText.slice(0, 200);

    return { before, after };
  }, [selection, localText]);

  const wordCount = localText.split(/\s+/).filter(Boolean).length;

  // ─── Guided writing brief detection ─────────────────────────────
  // Detect if the draft is a guided writing brief (has ## Paragraph or ## Section headers with brief fields)
  const isGuidedBrief = useMemo(() => {
    return /^##\s+(Paragraph|Section)/m.test(localText) && /\*\*Topic\*\*:/m.test(localText);
  }, [localText]);

  // Parse guided briefs into structured cards
  const guidedBriefs = useMemo(() => {
    if (!isGuidedBrief) return [];

    const briefs: Array<{
      heading: string;
      fields: Array<{ label: string; value: string }>;
    }> = [];

    // Split on ## headers
    const parts = localText.split(/^(## .+)$/m).filter(Boolean);
    let currentHeading = '';

    for (const part of parts) {
      if (part.startsWith('## ')) {
        currentHeading = part.replace('## ', '').trim();
      } else if (currentHeading) {
        // Parse bullet-point fields like - **Topic**: ...
        const fields: Array<{ label: string; value: string }> = [];
        const fieldRegex = /[-*]\s*\*\*([^*]+)\*\*:\s*(.+)/g;
        let match;
        while ((match = fieldRegex.exec(part)) !== null) {
          fields.push({ label: match[1].trim(), value: match[2].trim() });
        }
        if (fields.length > 0) {
          briefs.push({ heading: currentHeading, fields });
        }
        currentHeading = '';
      }
    }

    return briefs;
  }, [localText, isGuidedBrief]);

  // Guided mode: per-paragraph writing areas
  const [guidedDrafts, setGuidedDrafts] = useState<Record<number, string>>({});
  const [collapsedBriefs, setCollapsedBriefs] = useState<Set<number>>(new Set());

  const toggleBriefCollapse = useCallback((idx: number) => {
    setCollapsedBriefs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const handleGuidedDraftChange = useCallback((idx: number, text: string) => {
    setGuidedDrafts(prev => ({ ...prev, [idx]: text }));
  }, []);

  // Convert source cards to ContentItem shape for SourceCards component
  const sourceCardItems = sourceCards.map((card, i) => ({
    id: `source-${i}`,
    type: 'text' as const,
    text: card.excerpt,
    metadata: {
      source: { type: 'archive', path: [card.title] },
      wordCount: card.excerpt.split(/\s+/).filter(Boolean).length,
    },
  }));

  return (
    <div className="draft-editor-inline">
      {/* Header */}
      <div className="draft-editor-inline__header">
        <span className="draft-editor-inline__word-count">{wordCount.toLocaleString()} words</span>
        {isDirty && <span className="draft-editor-inline__dirty-dot" title="Unsaved changes" />}
        {generationModel && (
          <span className="draft-editor-inline__model-info" title={humanized ? `Generated by ${generationModel}, humanized by ${humanizationModel}` : `Generated by ${generationModel}`}>
            {generationModel}{humanized && humanizationModel && humanizationModel !== generationModel ? ` | Humanized: ${humanizationModel}` : humanized ? ' (humanized)' : ''}
          </span>
        )}
        {generationTimeMs != null && (
          <span className="draft-editor-inline__gen-time" title={humanizationTimeMs != null ? `Generation: ${(generationTimeMs / 1000).toFixed(1)}s, Humanization: ${(humanizationTimeMs / 1000).toFixed(1)}s` : undefined}>
            {(generationTimeMs / 1000).toFixed(1)}s{humanizationTimeMs != null ? ` + ${(humanizationTimeMs / 1000).toFixed(1)}s` : ''}
          </span>
        )}
        {targetWordCount != null && wordCount < targetWordCount * 0.85 && (
          <span className="draft-editor-inline__truncation-warning" title={`Draft has ${wordCount.toLocaleString()} words vs ${targetWordCount.toLocaleString()} target`}>
            May be truncated ({wordCount.toLocaleString()} / {targetWordCount.toLocaleString()})
          </span>
        )}
        <div className="draft-editor-inline__actions">
          {onViewWithSources && sourceCards.length > 0 && (
            <button
              className="draft-editor-inline__btn"
              onClick={() => onViewWithSources(localText, sourceCards, provenances)}
              title="View draft alongside source cards"
            >
              View with Sources
            </button>
          )}
          <button
            className="draft-editor-inline__btn"
            onClick={() => setShowSourceCards(!showSourceCards)}
            title={showSourceCards ? 'Hide sources' : 'Show sources'}
          >
            {sourceCards.length > 0 ? `Sources (${sourceCards.length})` : 'Sources'}
          </button>
        </div>
      </div>

      {/* Quality gate bar (Engine B constraint signals) */}
      {qualityReport && qualityReport.warnings.length > 0 && (
        <div className="draft-editor-inline__quality-bar">
          <div className="draft-editor-inline__quality-metrics">
            <span className="draft-editor-inline__quality-metric" title="Subjective Intentional Constraint score">
              SIC {(qualityReport.avgSicScore * 100).toFixed(0)}%
            </span>
            <span className="draft-editor-inline__quality-metric" title="Manager voice (structural filler)">
              Mgr {(qualityReport.avgManagerVoice * 100).toFixed(0)}%
            </span>
            <span className="draft-editor-inline__quality-metric" title="Scaffold sentence ratio">
              Scaffold {(qualityReport.scaffoldRatio * 100).toFixed(0)}%
            </span>
          </div>
          <div className="draft-editor-inline__quality-warnings">
            {qualityReport.warnings.map((w, i) => (
              <span key={i} className="draft-editor-inline__quality-pill">{w}</span>
            ))}
          </div>
        </div>
      )}

      {/* Intentional drift report (noema-noesis) */}
      {intentionalDrift && intentionalDrift.overallDrift > 0.1 && (
        <div className="draft-editor-inline__drift-bar">
          <div className="draft-editor-inline__drift-header">
            <span>Intentional Drift: {(intentionalDrift.overallDrift * 100).toFixed(0)}%</span>
            <span className="draft-editor-inline__drift-tension" title="Tension preserved">
              Tension {(intentionalDrift.tensionPreserved * 100).toFixed(0)}%
            </span>
          </div>
          {intentionalDrift.objectsLost.length > 0 && (
            <div className="draft-editor-inline__drift-lost">
              Lost: {intentionalDrift.objectsLost
                .sort((a, b) => b.salience - a.salience)
                .slice(0, 5)
                .map(o => o.label)
                .join(', ')}
            </div>
          )}
          {intentionalDrift.stanceShifts.length > 0 && (
            <div className="draft-editor-inline__drift-shifts">
              {intentionalDrift.stanceShifts.slice(0, 3).map((s, i) => (
                <span key={i} className="draft-editor-inline__drift-shift-pill">
                  {s.label}: {s.from} → {s.to}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Version bar (only when multiple versions exist) */}
      {versions.length > 1 && (
        <VersionBar
          versions={versions}
          activeVersionId={activeVersionId}
          onVersionSelect={handleVersionSelect}
          onVersionRestore={handleVersionRestore}
        />
      )}

      {/* Editor body — guided brief cards or plain textarea */}
      {isGuidedBrief && guidedBriefs.length > 0 ? (
        <div className="guided-writing">
          {guidedBriefs.map((brief, idx) => (
            <div key={idx} className="guided-writing__card">
              <button
                className="guided-writing__card-header"
                onClick={() => toggleBriefCollapse(idx)}
                aria-expanded={!collapsedBriefs.has(idx)}
              >
                <span className="guided-writing__card-number">{idx + 1}</span>
                <span className="guided-writing__card-heading">{brief.heading}</span>
                <span className="guided-writing__card-toggle">
                  {collapsedBriefs.has(idx) ? '\u25B6' : '\u25BC'}
                </span>
              </button>
              {!collapsedBriefs.has(idx) && (
                <>
                  <div className="guided-writing__brief-fields">
                    {brief.fields.map((field, fi) => (
                      <div key={fi} className="guided-writing__field">
                        <span className="guided-writing__field-label">{field.label}</span>
                        <span className="guided-writing__field-value">{field.value}</span>
                      </div>
                    ))}
                  </div>
                  <textarea
                    className="guided-writing__writing-area"
                    value={guidedDrafts[idx] ?? ''}
                    onChange={e => handleGuidedDraftChange(idx, e.target.value)}
                    placeholder="Write your paragraph here..."
                    rows={4}
                  />
                </>
              )}
            </div>
          ))}

          {/* Compiled draft from all writing areas */}
          {Object.values(guidedDrafts).some(v => v.trim().length > 0) && (
            <div className="guided-writing__compiled">
              <h4 className="guided-writing__compiled-label">Your Draft</h4>
              <p className="guided-writing__compiled-stats">
                {Object.values(guidedDrafts).join(' ').split(/\s+/).filter(Boolean).length} words
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="draft-editor-inline__body">
          <textarea
            ref={textareaRef}
            className="draft-editor-inline__textarea"
            value={localText}
            onChange={handleTextChange}
            onSelect={handleSelect}
            onMouseUp={handleSelect}
            placeholder="Your draft will appear here..."
            spellCheck
          />

          {/* Patch toolbar */}
          {selection && (
            <PatchToolbar
              selectedText={selection.text}
              anchorRect={selection.rect}
              context={getPatchContext()}
              onApply={handlePatchApply}
              onDismiss={() => setSelection(null)}
            />
          )}
        </div>
      )}

      {/* Source cards (collapsible) */}
      {showSourceCards && sourceCardItems.length > 0 && (
        <div className="draft-editor-inline__sources">
          <SourceCards items={sourceCardItems} />
        </div>
      )}
    </div>
  );
}

export default DraftEditorInline;
